<template>
  <div v-if="isVisible" class="pfea-overlay" @click.self="close" role="dialog" aria-modal="true">

    <div class="pfea-dialog" ref="modalContent" tabindex="-1">

      <!-- ═══ HEADER ═══ -->
      <div class="pfea-head">
        <div class="pfea-head-left">
          <div class="pfea-head-icon"><i class="ri-time-line"></i></div>
          <div>
            <div class="pfea-head-title">Commandes en file d'attente</div>
            <div class="pfea-head-sub">
              <span class="pfea-chip">{{ filteredData.length }} commande{{ filteredData.length > 1 ? 's' : '' }}</span>
              <span v-if="counts.local" class="pfea-chip pfea-chip-local">{{ counts.local }} locale{{ counts.local > 1 ? 's' : '' }}</span>
              <span v-if="counts.server" class="pfea-chip pfea-chip-srv">{{ counts.server }} serveur</span>
            </div>
          </div>
        </div>
        <button class="pfea-close" @click="close" aria-label="Fermer">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <!-- ═══ BARRE OUTILS ═══ -->
      <div class="pfea-toolbar">
        <div class="pfea-search-wrap">
          <i class="ri-search-line pfea-search-icon"></i>
          <input
            v-model="searchQuery"
            type="search"
            class="pfea-search"
            placeholder="Rechercher par client, n° doc, table..."
            @keyup.enter="selectFirstResult"
          />
          <button v-if="searchQuery" @click="clearSearch" class="pfea-search-clear">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="pfea-filter-group">
          <select v-model="filterSource" class="pfea-perpage-sel">
            <option value="">Toutes origines</option>
            <option value="local">Locales (non envoyées)</option>
            <option value="server">Serveur</option>
          </select>
          <select v-model="filterStatus" class="pfea-perpage-sel">
            <option value="">Tous statuts</option>
            <option value="pending">En attente</option>
            <option value="sent">Envoyée</option>
            <option value="failed">Erreur</option>
            <option value="completed">Comptabilisée</option>
          </select>
        </div>
        <div class="pfea-perpage">
          <span class="pfea-perpage-lbl">Afficher</span>
          <select v-model="perPage" class="pfea-perpage-sel">
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="150">150</option>
            <option :value="200">200</option>
          </select>
        </div>
        <button class="pfea-btn-sec pfea-refresh-btn" @click="refreshData(true)" :disabled="isLoading">
          <i :class="isLoading ? 'ri-loader-4-line pfea-state-spin' : 'ri-refresh-line'"></i>
          Rafraîchir
        </button>
      </div>

      <!-- ═══ FILTRE PÉRIODE ═══ -->
      <div class="pfea-period">
        <div class="pfea-period-presets">
          <button class="pfea-period-btn" :class="{ 'pfea-period-btn-active': activePreset === 'today' }" @click="setPreset('today')">Aujourd'hui</button>
          <button class="pfea-period-btn" :class="{ 'pfea-period-btn-active': activePreset === '3d' }" @click="setPreset('3d')">3 jours</button>
          <button class="pfea-period-btn" :class="{ 'pfea-period-btn-active': activePreset === '7d' }" @click="setPreset('7d')">7 jours</button>
          <button class="pfea-period-btn" :class="{ 'pfea-period-btn-active': activePreset === 'custom' }" @click="setPreset('custom')">Personnalisée</button>
        </div>
        <div class="pfea-period-range">
          <label class="pfea-period-field">
            <span class="pfea-period-lbl">Du</span>
            <input type="date" class="pfea-period-input" v-model="dateDebut" :max="dateFin" @change="onDateDebutChange" />
          </label>
          <label class="pfea-period-field">
            <span class="pfea-period-lbl">Au</span>
            <input type="date" class="pfea-period-input" v-model="dateFin" disabled title="Toujours égale à la date d'ouverture de caisse" />
          </label>
        </div>
      </div>

      <!-- ═══ BARRE DE PROGRESSION ═══ -->
      <transition name="pfea-progress-fade">
        <div v-if="isLoading && loadingStep" class="pfea-progress-bar">
          <div class="pfea-progress-inner">
            <i :class="loadingStepIcon" class="pfea-progress-ico pfea-state-spin"></i>
            <span class="pfea-progress-txt">{{ loadingStep }}</span>
            <span v-if="syncProgress.total > 0" class="pfea-progress-count">
              {{ syncProgress.done }}/{{ syncProgress.total }}
            </span>
          </div>
          <div class="pfea-progress-track">
            <div class="pfea-progress-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
        </div>
      </transition>

      <!-- ═══ COMPTEUR RÉSULTATS ═══ -->
      <div class="pfea-count-bar">
        <span class="pfea-count-num">{{ filteredData.length }}</span>
        résultat{{ filteredData.length > 1 ? 's' : '' }}
        <span v-if="searchQuery"> pour "{{ searchQuery }}"</span>
        <span
          class="pfea-lastfetch"
          :class="isServerFetchStale ? 'pfea-lastfetch-stale' : 'pfea-lastfetch-fresh'"
          :title="isServerFetchStale ? 'Cache expiré - le prochain refresh interrogera le serveur' : 'Cache valide - le serveur ne sera pas rappelé'"
        >
          <i class="ri-cloud-line"></i>
          Dernier fetch serveur : {{ lastServerFetchLabel }}
        </span>
      </div>

      <!-- ═══ BODY ═══ -->
      <div class="pfea-body">

        <!-- Loading -->
        <div v-if="isLoading" class="pfea-state">
          <i class="ri-loader-4-line pfea-state-spin pfea-state-icon"></i>
          <p>Chargement des commandes…</p>
        </div>

        <!-- Error -->
        <div v-else-if="errorMessage && !hasAnyData" class="pfea-state">
          <i class="ri-error-warning-line pfea-state-icon pfea-state-err"></i>
          <p>{{ errorMessage }}</p>
          <button class="pfea-btn-sec" @click="refreshData">
            <i class="ri-refresh-line"></i> Réessayer
          </button>
        </div>

        <!-- Table -->
        <table v-else class="pfea-table">
          <thead>
            <tr>
              <th class="pfea-th pfea-th-num">N°</th>
              <th class="pfea-th">N° Document<br/>(local/serveur)</th>
              <th class="pfea-th">Client</th>
              <th class="pfea-th pfea-col-hide-sm">Table</th>
              <th class="pfea-th pfea-col-hide-sm">Serveur</th>
              <th class="pfea-th pfea-th-right">Montant</th>
              <th class="pfea-th pfea-th-center pfea-col-hide-md">Statut</th>
              <th class="pfea-th pfea-col-hide-md">Origine</th>
              <th class="pfea-th pfea-col-hide-md">Date caisse</th>
              <th class="pfea-th pfea-col-hide-md">Date</th>
              <th class="pfea-th pfea-th-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="paginatedData.length">
              <tr
                v-for="(item, index) in paginatedData"
                :key="item.id || index"
                :class="['pfea-tr', isCompletedRow(item) ? 'pfea-tr-done' : '']"
                @dblclick="!isCompletedRow(item) && selectOrder(item)"
              >
                <td class="pfea-td">
                  <span class="pfea-num-badge">{{ (currentPage - 1) * perPage + index + 1 }}</span>
                </td>
                <td class="pfea-td">
                  <span class="pfea-doc-badge">{{ item.pidindexedb  }}/{{ item.numdoc }}</span>
                </td>
                <td class="pfea-td">
                  <div class="pfea-client-row">
                    <div class="pfea-avatar">{{ getInitials(item.client) }}</div>
                    <span class="pfea-client-name">{{ item.client || '-' }}</span>
                  </div>
                </td>
                <td class="pfea-td pfea-col-hide-sm">
                  <span v-if="item.commandetable" class="pfea-table-badge">
                    <i class="ri-layout-grid-line"></i> {{ item.commandetable }}
                  </span>
                  <span v-else class="pfea-dash">-</span>
                </td>
                <td class="pfea-td pfea-col-hide-sm">
                  <span class="pfea-text-muted">{{ item.serveur || '-' }}</span>
                </td>
                <td class="pfea-td pfea-td-right">
                  <span class="pfea-amount">{{ formatCurrency(item.mtntcc) }}</span>
                </td>
                <td class="pfea-td pfea-td-center pfea-col-hide-md">
                  <span :class="statusBadgeClass(item)" :title="item.errorMessage || ''">
                    <span v-if="item.status === 'pending'" class="pfea-status-dot pfea-dot-pending"></span>
                    <span v-if="item.status === 'sent'" class="pfea-status-dot pfea-dot-sent"></span>
                    <span v-if="item.status === 'failed'" class="pfea-status-dot pfea-dot-failed"></span>
                    <span v-if="item.status === 'completed'" class="pfea-status-dot pfea-dot-done"></span>
                    {{ statusLabel(item.status) }}
                  </span>
                </td>
                <td class="pfea-td pfea-col-hide-md">
                  <span v-if="item.source === 'local'" class="pfea-origin-badge pfea-origin-local">
                    <i class="ri-smartphone-line"></i> Local
                  </span>
                  <span v-else class="pfea-origin-badge pfea-origin-server">
                    <i class="ri-cloud-line"></i> Serveur
                  </span>
                </td>
                <td class="pfea-td pfea-col-hide-md pfea-text-muted">{{ item.datecaisse || '-' }}</td>
                <td class="pfea-td pfea-col-hide-md pfea-text-muted">{{ item.creerler || item.datecreation || '-' }}</td>
                <td class="pfea-td pfea-td-center">
                  <div class="pfea-action-group">
                    <button
                      class="pfea-load-btn"
                      @click.stop="selectOrder(item)"
                      :disabled="isCompletedRow(item)"
                      :title="isCompletedRow(item) ? 'Déjà comptabilisée' : 'Charger la commande'"
                    >
                      <i class="ri-upload-2-line"></i>
                      <span>Charger</span>
                    </button>
                    <button
                      v-if="item.source === 'local'"
                      class="pfea-del-btn"
                      @click.stop="confirmDeleteLocal(item)"
                      :disabled="busyRowId === item.id"
                      :title="deleteBtnTitle(item)"
                    >
                      <i :class="busyRowId === item.id ? 'ri-loader-4-line pfea-spin' : 'ri-delete-bin-line'"></i>
                    </button>
                  </div>

                  <!-- Commande encore porteuse de lignes côté serveur : la
                       suppression est refusée tant qu'elles ne sont pas
                       retirées (retour en stock compris). -->
                  <div v-if="lignesBloquantes[item.id] > 0" class="pfea-block-lignes">
                    <div class="pfea-block-msg">
                      <i class="ri-error-warning-line"></i>
                      {{ lignesBloquantes[item.id] }} ligne(s) sur cette commande
                    </div>
                    <button class="pfea-supp-lignes-btn"
                            @click.stop="confirmSupprimerLignes(item)"
                            :disabled="busyRowId === item.id">
                      <i class="ri-eraser-line"></i>
                      Supprimer les lignes d'abord
                    </button>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="11">
                <div class="pfea-state">
                  <i class="ri-file-list-3-line pfea-state-icon"></i>
                  <p>{{ searchQuery ? 'Aucun résultat' : 'La file d\'attente est vide' }}</p>
                  <small v-if="searchQuery">Essayez de modifier votre recherche</small>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

      </div>

      <!-- ═══ FOOTER / PAGINATION ═══ -->
      <div class="pfea-foot">
        <div class="pfea-page-info">
          Page <strong>{{ currentPage }}</strong> / <strong>{{ totalPages || 1 }}</strong>
          <span class="pfea-page-total">({{ filteredData.length }} élément{{ filteredData.length > 1 ? 's' : '' }})</span>
        </div>
        <div class="pfea-page-nav">
          <button
            class="pfea-page-btn"
            @click="currentPage--"
            :disabled="currentPage === 1"
          ><i class="ri-arrow-left-s-line"></i></button>

          <template v-for="pageNum in displayedPages" :key="pageNum">
            <span v-if="pageNum === '...'" class="pfea-page-dots">…</span>
            <button
              v-else
              class="pfea-page-btn"
              :class="{ 'pfea-page-btn-active': currentPage === pageNum }"
              @click="currentPage = pageNum"
            >{{ pageNum }}</button>
          </template>

          <button
            class="pfea-page-btn"
            @click="currentPage++"
            :disabled="currentPage >= totalPages"
          ><i class="ri-arrow-right-s-line"></i></button>

          <button class="pfea-btn-sec" @click="close">Fermer</button>
        </div>
      </div>

    </div>

    <!-- Child modal suppression -->
    <ModalFormSuppression ref="modalFormSuppression" @closed="onDeleteConfirmed" />

  </div>
</template>

<script>
    const axios = require('axios');
    import { currencyFormatDE, randomString } from '@/allfunction';
    import ModalFormSuppression from '@/components/views/pages/gestioncommerciale/achatvente/default/formsuppression';
    import * as JwtService from '@/common/jwt.service';
    import {
        getAllPendingOrders, removeFromQueue, addToQueue,
        updateQueueDataItems, purgeQueueKeepFailed
    } from '@/service/orderQueueService';

    export default {
        /* eslint-disable */
        name: 'OrderQueueModal',

        components: {
            ModalFormSuppression,
        },

        emits: ['selectedfacture'],

        data() {
            return {
                isVisible: false,
                isLoading: false,
                lastServerFetch: null,
                nowTick: Date.now(),   // horloge réactive pour le "il y a Xs"
                tickTimer: null,
                loadingStep: '',       // texte de l'étape en cours
                syncProgress: { done: 0, total: 0 },  // compteur sync
                errorMessage: '',
                dataTable: [],
                localOrders: [],
                searchQuery: '',
                filterSource: '',
                filterStatus: '',
                dateDebut: '',
                dateFin: '',
                activePreset: 'today',
                currentPage: 1,
                perPage: 50,
                searchDebounceTimer: null,
                deleteTarget: null,
                /* Suppression d'une commande de la file.
                   busyRowId       : ligne en cours de traitement (spinner)
                   lignesBloquantes: { [item.id]: nb } - nombre de lignes encore
                                     rattachées au document serveur, qui interdit
                                     la suppression de la commande. */
                busyRowId: null,
                lignesBloquantes: {},
            };
        },

        computed: {
            hasAnyData() {
                return this.dataTable.length > 0 || this.localOrders.length > 0;
            },

            // Date d'ouverture de caisse, normalisée en YYYY-MM-DD (= borne haute de la période)
            dateOuverture() {
                return toISODate(this.$store.state.caisseinfo && this.$store.state.caisseinfo.dateouverture);
            },

            loadingStepIcon() {
                if (this.loadingStep.includes('serveur')) return 'ri-cloud-line pfea-progress-ico';
                if (this.loadingStep.includes('sync') || this.loadingStep.includes('Sync')) return 'ri-refresh-line pfea-progress-ico';
                return 'ri-database-2-line pfea-progress-ico';
            },

            progressPercent() {
                if (this.syncProgress.total > 0) {
                    return Math.round((this.syncProgress.done / this.syncProgress.total) * 100);
                }
                // Étapes sans compteur : 33% → 66% → 100%
                if (this.loadingStep.includes('local') || this.loadingStep.includes('IndexedDB')) return 33;
                if (this.loadingStep.includes('serveur')) return 66;
                if (this.loadingStep.includes('Mise à jour') || this.loadingStep.includes('terminé')) return 100;
                return 20;
            },

            lastServerFetchLabel() {
                if (!this.lastServerFetch) return 'jamais';
                const d = new Date(this.lastServerFetch);
                const hh = String(d.getHours()).padStart(2, '0');
                const mm = String(d.getMinutes()).padStart(2, '0');
                const ss = String(d.getSeconds()).padStart(2, '0');
                const elapsed = Math.max(0, Math.round((this.nowTick - this.lastServerFetch) / 1000));
                return `${hh}:${mm}:${ss} (il y a ${elapsed}s)`;
            },

            isServerFetchStale() {
                if (!this.lastServerFetch) return true;
                return (this.nowTick - this.lastServerFetch) > TTL_MS;
            },

            counts() {
                return {
                    local: this.localOrders.filter(o => o.status !== 'completed').length,
                    server: this.dataTable.length,
                };
            },

            mergedData() {
                const list = [];

                // Index des pids serveur déjà présents en IndexedDB (pour déduplication)
                // serverDocId vit dans orderData (payload de addToQueue), fallback racine
                const localServerPids = new Set(
                    this.localOrders
                        .map(o => o.serverDocId || o.orderData?.serverDocId)
                        .filter(Boolean)
                );

                for (const order of this.localOrders) {

                 // console.log('order order:', order);



                    const od   = order.orderData || {};
                    const mtnt = od.mtnt || {};
                    list.push({
                        id:           'l-' + order.localId,
                        numdoc:       od.srvdocnum || '-',
                        client:       od.client    || '-',
                        commandetable: od.commandetable || '-',
                        serveur:      od.serveur   || '-',
                        mtntcc:       mtnt.docmtnttotalttc || 0,
                        saisiepar:    '',
                        creerler:     formatDateFromTs(order.createdAt),
                        status:       order.status,
                        source:       'local',
                        pid:          order.serverDocId || od.serverDocId || '',
                        localId:      order.localId,
                        pidindexedb:  od.pidindexedb,
                        orderData:    od,
                        errorMessage: order.errorMessage || '',
                        datecreation: formatDateFromTs(order.createdAt),
                        datecaisseISO: toISODate(od.datedoc || order.createdAt),
                        datecaisse:    formatISOtoFR(od.datedoc || order.createdAt),
                    });
                }

                // Docs serveur : ignorer ceux déjà synchés en IndexedDB
                for (const doc of this.dataTable) {
                    if (doc.pid && localServerPids.has(doc.pid)) continue;
                    list.push({
                        ...doc,
                        id:     's-' + (doc.pid || doc.numdoc || ''),
                        source: 'server',
                       // status: 'completed',
                        status: 'pending',
                        datecaisseISO: toISODate(doc.datedoc),
                        datecaisse:    formatISOtoFR(doc.datedoc),
                    });
                }

                return list;
            },

            filteredData() {
                let list = this.mergedData;

                // Filtre PÉRIODE en premier : [dateDebut, dateFin] sur la date caisse (datedoc)
                if (this.dateDebut && this.dateFin) {
                    list = list.filter(item => {
                        const d = item.datecaisseISO;
                        return d && d >= this.dateDebut && d <= this.dateFin;
                    });
                }

                if (this.filterSource) {
                    list = list.filter(item => item.source === this.filterSource);
                }
                if (this.filterStatus) {
                    list = list.filter(item => item.status === this.filterStatus);
                }

                if (!this.searchQuery.trim()) {
                    return list;
                }

                const query = this.searchQuery.toLowerCase().trim();
                return list.filter(item => {
                    const client = (item.client || '').toLowerCase();
                    const numdoc = (item.numdoc || '').toLowerCase();
                    const pidindexedb = (item.pidindexedb || '').toLowerCase();
                    const table = (item.commandetable || '').toLowerCase();
                    const serveur = (item.serveur || '').toLowerCase();
                    return (
                        client.includes(query) ||
                        numdoc.includes(query) ||
                        pidindexedb.includes(query) ||
                        table.includes(query) ||
                        serveur.includes(query)
                    );
                });
            },

            paginatedData() {
                const start = (this.currentPage - 1) * this.perPage;
                return this.filteredData.slice(start, start + this.perPage);
            },

            totalPages() {
                return Math.ceil(this.filteredData.length / this.perPage) || 1;
            },

            displayedPages() {
                const pages = [];
                const total = this.totalPages;
                const current = this.currentPage;

                if (total <= 7) {
                    for (let i = 1; i <= total; i++) pages.push(i);
                } else {
                    if (current <= 4) {
                        pages.push(1, 2, 3, 4, 5, '...', total);
                    } else if (current >= total - 3) {
                        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
                    } else {
                        pages.push(1, '...', current - 1, current, current + 1, '...', total);
                    }
                }
                return pages;
            },
        },

        watch: {
            searchQuery() {
                clearTimeout(this.searchDebounceTimer);
                this.searchDebounceTimer = setTimeout(() => {
                    this.currentPage = 1;
                }, 150);
            },
            perPage() { this.currentPage = 1; },
            filterSource() { this.currentPage = 1; },
            filterStatus() { this.currentPage = 1; },
            dateDebut() { this.currentPage = 1; },
            dateFin() { this.currentPage = 1; },
        },

        methods: {
            // ============ MODAL METHODS ============
            show() {
                this.searchQuery   = '';
                this.filterSource  = '';
                this.filterStatus  = '';
                // Période par défaut : Aujourd'hui → datedebut = datefin = date d'ouverture de caisse
                this.setPreset('today');
                this.currentPage   = 1;
                this.errorMessage  = '';
                this.isLoading     = false;   // reset propre à chaque ouverture
                this.loadingStep   = '';
                this.syncProgress  = { done: 0, total: 0 };
                // Recharger le timestamp persisté (survit aux remontages / F5)
                this.lastServerFetch = Number(localStorage.getItem(FETCH_KEY) || 0) || null;
                // Horloge : met à jour le "il y a Xs" chaque seconde tant que la modale est ouverte
                clearInterval(this.tickTimer);
                this.tickTimer = setInterval(() => { this.nowTick = Date.now(); }, 1000);
                this.nowTick = Date.now();
                this.isVisible     = true;
                document.body.style.overflow = 'hidden';
                this.$nextTick(async() => {
                    this.$refs.modalContent?.focus();
                    // await this.loadServerOrders();
                });
                this.refreshData();
            },

            close() {
                this.isVisible = false;
                document.body.style.overflow = '';
                clearInterval(this.tickTimer);
                this.tickTimer = null;
            },

            // ============ DATA METHODS ============
            async refreshData(force = false) {
                this.isLoading = true;
                this.loadingStep = '';
                this.errorMessage = '';
                this.syncProgress = { done: 0, total: 0 };
                try {

                    // Étape 1 : IndexedDB en premier → affichage immédiat. localOrders
                    // n'est jamais vidé : il sert aussi de base de déduplication à
                    // syncServerToIndexedDB, qui recréerait sinon tous les docs serveur.
                    this.loadingStep = 'Chargement local (IndexedDB)…';
                    await this.loadLocalOrders();

                    // Étape 2 : Serveur - uniquement si forcé ou TTL expiré (45s)
                    // Timestamp persisté en localStorage : survit aux remontages, navigations et F5
                    const last = Number(localStorage.getItem(FETCH_KEY) || 0);
                    const isStale = !last || (Date.now() - last) > TTL_MS;

                    if (force || isStale) {

                        // Purge sélective : seules les commandes 'failed' sont
                        // conservées. Les 'pending' ont été acceptées par le serveur
                        // et reviennent par la synchro ci-dessous ; les 'failed' n'ont
                        // aucune contrepartie serveur et seraient perdues.
                        this.loadingStep = 'Nettoyage des commandes…';
                        await purgeQueueKeepFailed();
                        await this.loadLocalOrders();

                        this.loadingStep = 'Connexion au serveur…';
                        const serverDocs = await this.loadServerOrders();

                        // Étape 3 : Sync serveur → IndexedDB (nouveau device / autre navigateur)
 
                        if (serverDocs.length > 0) {
                            this.loadingStep = 'Synchronisation IndexedDB…';
 
                            await this.syncServerToIndexedDB(serverDocs);
                            // Étape 4 : Recharger IndexedDB pour intégrer les docs synchés
                            this.loadingStep = 'Mise à jour de la liste…';
                           // console.log('Rechargement local après sync serveur → IndexedDB');
                            await this.loadLocalOrders();
                        }

                        this.lastServerFetch = Date.now();
                        localStorage.setItem(FETCH_KEY, String(Date.now()));
                    }
                    else {
                        this.loadingStep = 'Données à jour (cache local)…';
                    }

                    // Laisser "terminé" visible 500ms avant de fermer la barre
                    this.loadingStep = 'Chargement terminé ✓';
                    await new Promise(r => setTimeout(r, 500));

                }
                 catch (e) {
                    console.error('Erreur chargement:', e);
                    this.loadingStep = '';
                    if (!this.hasAnyData) {
                        this.errorMessage = 'Impossible de charger les commandes.';
                    }
                } 
                finally {
                    this.isLoading = false;
                    this.loadingStep = '';
                    this.syncProgress = { done: 0, total: 0 };
                }
            },

            async loadLocalOrders() {
                try {
                 // console.log('Chargement des commandes locales depuis IndexedDB…');
                    this.localOrders = await getAllPendingOrders();

                    /*console.log('Commandes locales chargées:', this.localOrders.length);
                    console.log('Commandes locales:', this.localOrders);*/
                } catch (e) {
                    console.error('Erreur IndexedDB:', e);
                    this.localOrders = [];
                }
            },

            async loadServerOrders() {
                try {
                    const caisse = {
                        cf: this.$store.state.caisseinfo,
                        document: 'FV',
                    };
                    const url = `${process.env.VUE_APP_API_URL}/egl74587kjkjrfdkcaisse771714dfe/comptoire/caisse/${this.$store.state.caisseencour}/${this.$store.state.appsociete}/load895418init51comptoirerefdepdpcc11515/indexdb/option2/${randomString(280)}`;
                    const response = await axios.post(url, caisse, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete,
                        },
                        withCredentials: true,
                    });
                    if (response.data.success) {
                        this.dataTable = response.data.m || [];
                        return this.dataTable;
                    }
                    this.dataTable = [];
                    if (!this.hasAnyData) {
                        this.errorMessage = response.data.message || 'Erreur lors du chargement des données';
                    }
                    return [];
                } catch (e) {
                    console.error('Erreur API:', e);
                    this.dataTable = [];
                    if (!this.hasAnyData) {
                        this.errorMessage = 'Impossible de charger les commandes. Vérifiez votre connexion.';
                    }
                    return [];
                }
            },

            // Synchronise les docs serveur absents d'IndexedDB → utile sur nouveau device
            async syncServerToIndexedDB(serverDocs) {
 
                const existingPids = new Set(
                    this.localOrders
                        .map(o => o.serverDocId || o.orderData?.serverDocId)
                        .filter(Boolean)
                );
                const existingNumdocs = new Set(
                    this.localOrders
                        .map(o => o.srvdocnum || o.orderData?.srvdocnum)
                        .filter(Boolean)
                );

                // Compter uniquement les docs qui seront réellement synchés
                const toSync = serverDocs.filter(doc =>
                    !existingPids.has(doc.pid) && !existingNumdocs.has(doc.numdoc)
                );

               // console.log('Docs serveur à synchroniser vers IndexedDB:', toSync.length, toSync);

                this.syncProgress = { done: 0, total: toSync.length };
 

                for (const doc of toSync) { 
 
                 // console.log("entrer for")
 
                    this.loadingStep = `Sync : ${doc.numdoc || doc.pid}…`;
                    //const localId = 'srv-' + doc.pid;
                    const localId = 'srv-' + doc.pidindexedb;
                    const payload = this._buildServerPayload(doc);
  
                    try {
                      let $res=  await addToQueue(localId, payload);

                     // console.log('addToQueue result:', $res);
 

                        //await markQueueCompleted(localId, doc.numdoc, doc.pid);
                    } catch (e) {
                        console.warn('[sync] Échec pour', doc.numdoc, e);
                    }
                    this.syncProgress.done++;
                }
            },

            // Construit un orderPayload compatible IndexedDB depuis un doc serveur
            _buildServerPayload(doc) {
                const client = typeof doc.client === 'object'
                    ? (doc.client?.name ?? '')
                    : (doc.client ?? '');
                const mtnt = doc.mtnt || {};
  
                return {
                    //pidindexedb:            doc.pidindexedb    || '',
                    pidindexedb:            'srv-' + doc.pidindexedb,
                    datedoc:                doc.datedoc || '',
                    srvdocnum:              doc.numdoc || '',
                    srvpid:                 doc.pid    || '',
                    serverDocId:            doc.pid    || '',
                    client,
                    commandetable:          doc.commandetable  || '',
                    serveur:                doc.serveur  || '',
                    createdAt:                doc.createdat  || Date.now(),
                    mtnt: {
                        docmtnttotalttc: parseFloat(mtnt.mtntcc   || 0),
                        docmtnttotalht:  parseFloat(mtnt.mtntht  || 0),
                        docmtntremise:   parseFloat(mtnt.remise  ||  0),
                    },
                    Items: doc,
                    jsonv:                  doc.jsonv          || {},
                    jsonotherdonne:         doc.jsonotherdonne || {},
                    listedatatable:         [],
                    listedatatableindexedb: [],
                    cf:                     this.$store.state.caisseinfo,
                    _syncedFromServer:      true,
                    source:'serveur'
                };
            },

            // ============ SELECTION ============
            async selectOrder(item) {

console.log('selectOrder item:', item);

                if (this.isCompletedRow(item)) return;
 

//console.log('selectOrder item.orderData:', item.orderData);
//console.log('selectOrder item.orderData:', item.orderData);
//console.log('selectOrder item._syncedFromServer:', item.orderData._syncedFromServer);
//console.log('selectOrder item.orderData && item.orderData._syncedFromServer:', item.orderData && item.orderData._syncedFromServer);
                if (/*item.source === 'local' &&*/ item.orderData && item.orderData._syncedFromServer) {
                    // Item syncé depuis le serveur → charger les articles avant d'émettre
                    await this.loadArticlesAndEmit(item);

                } 
                else if (/*item.source === 'local' &&*/ item.orderData) {
                    // Item local ordinaire
                    this.$emit('selectedfacture', {
                        source: 'local',
                        localId: item.localId,
                        orderData: item.orderData,
                    });

                } 
                else {
                    this.$emit('selectedfacture', {
                        source: 'server',
                        item: item,
                    });
                }
                this.close();
            },

            async loadArticlesAndEmit(item) {
                try {
                    this.isLoading = true;
                    this.loadingStep = 'Chargement des articles…';

                    const pid = item.pid;
                    const url = `${process.env.VUE_APP_API_URL}/egl74587kjkjrfdkcaisse771714dfe/comptoire/caisse/${pid}/${this.$store.state.appsociete}/allline895418init51comptoirerefdepdpcc11515/option2/${randomString(280)}`;

                    const response = await axios.get(url, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete,
                        },
                        withCredentials: true,
                    });

                    if (response.data.success) {
                        const articles = response.data.m || [];


                        ///console.log('Articles chargés depuis le serveur:', articles);

                        for (const art of articles) {
                            // Convertir les champs numériques
                          //  console.log('Articles chargés  art:', art);
                        }




                        // Mettre à jour IndexedDB avec les articles
                        await updateQueueDataItems(item.localId, articles);

                        // Enrichir orderData en mémoire
                        const enrichedOrderData = {
                            ...item.orderData,
                            listedatatable:         articles,
                           // listedatatableindexedb: articles,
                            listedatatableindexedb: [],
                        };

                        this.$emit('selectedfacture', {
                            source: 'local',
                            localId: item.localId,
                            orderData: enrichedOrderData,
                        });
                    } else {
                        // Fallback : émettre sans articles
                        this.$emit('selectedfacture', {
                            source: 'local',
                            localId: item.localId,
                            orderData: item.orderData,
                        });
                    }
                } catch (e) {
                    console.error('[loadArticlesAndEmit] Erreur:', e);
                    // Fallback silencieux
                    this.$emit('selectedfacture', {
                        source: 'local',
                        localId: item.localId,
                        orderData: item.orderData,
                    });
                } finally {
                    this.isLoading = false;
                    this.loadingStep = '';
                }
            },

            selectFirstResult() {
                if (this.paginatedData.length > 0) {
                    const first = this.paginatedData[0];
                    if (!this.isCompletedRow(first)) this.selectOrder(first);
                }
            },

            clearSearch() {
                this.searchQuery = '';
            },

            // ============ PÉRIODE ============
            // datefin TOUJOURS = date d'ouverture de caisse ; on ne fait varier que datedebut.
            setPreset(preset) {
                const fin = this.dateOuverture;
                this.dateFin = fin;
                if (preset === 'today') {
                    this.dateDebut = fin;
                } else if (preset === '3d') {
                    this.dateDebut = addDaysISO(fin, -2);   // 3 jours inclus (aujourd'hui + 2 avant)
                } else if (preset === '7d') {
                    this.dateDebut = addDaysISO(fin, -6);   // 7 jours inclus
                } else if (preset === 'custom') {
                    // conserve la datedebut courante, bornée à <= datefin
                    if (!this.dateDebut || this.dateDebut > fin) this.dateDebut = fin;
                }
                this.activePreset = preset;
                this.currentPage = 1;
            },

            // datedebut éditable : on borne à <= date d'ouverture et on passe en mode personnalisé
            onDateDebutChange() {
                const fin = this.dateOuverture;
                this.dateFin = fin;
                if (this.dateDebut && this.dateDebut > fin) this.dateDebut = fin;
                this.activePreset = 'custom';
                this.currentPage = 1;
            },

            // ============ DELETE ============

            /* Une commande en erreur n'a jamais abouti côté serveur : il n'y a
               rien à défaire, on la retire directement de la file.
               Une commande en attente / envoyée, elle, a un document serveur
               qui peut porter des lignes déjà déstockées : on ne la supprime
               pas tant que ces lignes n'ont pas été retirées. */
            estSupprimableSansControle(item) {
                return item.status === 'failed' || !item.pid;
            },

            deleteBtnTitle(item) {
                if (this.busyRowId === item.id) return 'Traitement en cours…';
                if (this.estSupprimableSansControle(item)) return 'Supprimer de la file';
                return 'Supprimer de la file (les lignes du document seront contrôlées)';
            },

            urlSuppLignes() {
                return `${process.env.VUE_APP_API_URL}/egl74587kjkjrfdkcaisse771714dfe/comptoire/caisse/`
                     + `${this.$store.state.caisseencour}/${this.$store.state.appsociete}`
                     + `/supplignes895418init51comptoirerefdepdpcc11515/option2/${randomString(280)}`;
            },

            /* Appel de la route dédiée à la suppression des lignes.
               mode = 'check'  → décompte seul, ne touche à rien
               mode = 'delete' → suppression effective + retour en stock */
            async appelLignesServeur(item, mode) {
                const body = {
                    mode,
                    docpid: item.pid || '',
                    pidindexedb: item.pidindexedb || item.localId || '',
                };
                const { data } = await axios.post(this.urlSuppLignes(), body, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${JwtService.getToken()}`,
                        'csrftoken': this.$store.state.appsociete,
                    },
                    withCredentials: true,
                });
                return data || {};
            },

            async confirmDeleteLocal(item) {

                // Cas simple : rien côté serveur, suppression locale directe.
                if (this.estSupprimableSansControle(item)) {
                    this.deleteTarget = item;
                    const title = `Supprimer la commande ${item.numdoc} de la file d'attente ?`;
                    this.$refs.modalFormSuppression.show(title, item, 'F', 'attente');
                    return;
                }

                // Sinon : on contrôle d'abord les lignes du document serveur.
                this.busyRowId = item.id;
                try {
                    const res = await this.appelLignesServeur(item, 'check');

                    if (!res.success) {
                        toastr['error'](res.message || 'Contrôle des lignes impossible.', '<b>Suppression refusée</b>');
                        return;
                    }

                    const nb = parseInt(res.nblignes || 0, 10);

                    if (nb > 0) {
                        this.lignesBloquantes = { ...this.lignesBloquantes, [item.id]: nb };
                        const apercu = (res.lignes || []).slice(0, 4).join(', ');
                        toastr['warning'](
                            `Cette commande porte encore <b>${nb} ligne(s)</b> de produit`
                            + (apercu ? ` (${apercu}${nb > 4 ? '…' : ''})` : '')
                            + `.<br>Supprimez d'abord toutes les lignes avant de supprimer la commande.`,
                            '<b>Suppression refusée</b>',
                            { timeOut: 9000 }
                        );
                        return;
                    }

                    // Plus aucune ligne : la suppression peut suivre son cours.
                    const copie = { ...this.lignesBloquantes };
                    delete copie[item.id];
                    this.lignesBloquantes = copie;

                    this.deleteTarget = item;
                    const title = `Supprimer la commande ${item.numdoc} de la file d'attente ?`;
                    this.$refs.modalFormSuppression.show(title, item, 'F', 'attente');

                } catch (e) {
                    console.error('[file attente] contrôle des lignes', e);
                    toastr['error']('Contrôle des lignes impossible : serveur injoignable.', '<b>Erreur</b>');
                } finally {
                    this.busyRowId = null;
                }
            },

            /* Retrait de toutes les lignes du document, avec retour en stock.
               Passe par la route dédiée, jamais par la route d'envoi. */
            async confirmSupprimerLignes(item) {
                const nb = this.lignesBloquantes[item.id] || 0;
                const ok = window.confirm(
                    `Supprimer les ${nb} ligne(s) de la commande ${item.numdoc} ?\n\n`
                    + `Les quantités des articles gérés en stock seront restituées.\n`
                    + `Cette opération est irréversible.`
                );
                if (!ok) return;

                this.busyRowId = item.id;
                try {
                    const res = await this.appelLignesServeur(item, 'delete');

                    if (res.success) {
                        const copie = { ...this.lignesBloquantes };
                        delete copie[item.id];
                        this.lignesBloquantes = copie;
                        toastr['success'](
                            res.message || 'Lignes supprimées, stock restitué.',
                            '<b>Lignes retirées</b>'
                        );
                    } else {
                        this.lignesBloquantes = {
                            ...this.lignesBloquantes,
                            [item.id]: parseInt(res.nbrestantes || res.nblignes || 0, 10),
                        };
                        toastr['error'](
                            (res.message || 'Suppression des lignes incomplète.')
                            + ((res.datamessage || []).length ? '<br>' + res.datamessage.join('<br>') : ''),
                            '<b>Erreur</b>', { timeOut: 9000 }
                        );
                    }
                } catch (e) {
                    console.error('[file attente] suppression des lignes', e);
                    toastr['error']('Suppression des lignes impossible : serveur injoignable.', '<b>Erreur</b>');
                } finally {
                    this.busyRowId = null;
                }
            },

            async onDeleteConfirmed(deleted) {
                if (deleted && this.deleteTarget && this.deleteTarget.localId) {
                    try {
                        await removeFromQueue(this.deleteTarget.localId);
                        this.localOrders = this.localOrders.filter(o => o.localId !== this.deleteTarget.localId);
                    } catch (e) {
                        console.error('Erreur suppression:', e);
                    }
                }
                this.deleteTarget = null;
            },

            // ============ HELPERS ============
            isCompletedRow(item) {
                return item.status === 'completed';
                
            },

            statusLabel(status) {
                const map = {
                    pending: 'En attente',
                    sent: 'Envoyée',
                    failed: 'Erreur',
                    completed: 'Comptabilisée',
                };
                return map[status] || status || '-';
            },

            statusBadgeClass(item) {
                return {
                    'pfea-status-badge': true,
                    'pfea-status-pending': item.status === 'pending',
                    'pfea-status-sent': item.status === 'sent',
                    'pfea-status-failed': item.status === 'failed',
                    'pfea-status-done': item.status === 'completed',
                };
            },

            formatCurrency(value) {
                return currencyFormatDE(value);
            },

            getInitials(name) {
                if (!name || name === '-') return '?';
                const parts = String(name).split(' ');
                return parts.map(w => w[0]).join('').substring(0, 2).toUpperCase();
            },

            handleKeydown(event) {
                if (event.key === 'Escape' && this.isVisible) {
                    this.close();
                }
            },
        },

        mounted() {
            document.addEventListener('keydown', this.handleKeydown);
        },

        beforeUnmount() {
            document.removeEventListener('keydown', this.handleKeydown);
            document.body.style.overflow = '';
            clearTimeout(this.searchDebounceTimer);
            clearInterval(this.tickTimer);
        },
    };

    // Clé localStorage du timestamp du dernier fetch serveur (TTL persistant)
    const FETCH_KEY = 'orderQueue_lastServerFetch';
    // Durée de validité du cache serveur (45s)
    const TTL_MS = 45_000;

    function formatDateFromTs(ts) {
        if (!ts) return '';
        const d = new Date(ts);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(2);
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yy} ${hh}:${min}`;
    }

    // Convertit en timestamp ms → YYYY-MM-DD (heure locale)
    function tsToISO(ts) {
        const d = new Date(ts);
        if (isNaN(d)) return '';
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
    }

    // Normalise une date (YYYY-MM-DD, "YYYY-MM-DD HH:mm:ss", DD/MM/YYYY, timestamp) → YYYY-MM-DD
    function toISODate(v) {
        if (!v && v !== 0) return '';
        if (typeof v === 'number') return tsToISO(v);
        const s = String(v).trim();
        if (!s) return '';
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}-${m[3]}`;
        const m2 = s.match(/^(\d{2})\/(\d{2})\/(\d{2,4})/);
        if (m2) {
            let y = m2[3];
            if (y.length === 2) y = '20' + y;
            return `${y}-${m2[2]}-${m2[1]}`;
        }
        const d = new Date(s);
        return isNaN(d) ? '' : tsToISO(d.getTime());
    }

    // YYYY-MM-DD → DD/MM/YYYY (pour l'affichage)
    function formatISOtoFR(v) {
        const iso = toISODate(v);
        if (!iso) return '-';
        const [y, m, d] = iso.split('-');
        return `${d}/${m}/${y}`;
    }

    // Décale une date ISO de n jours (sans souci de fuseau horaire)
    function addDaysISO(iso, n) {
        if (!iso) return iso;
        const [y, m, d] = iso.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + n);
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        return `${dt.getFullYear()}-${mm}-${dd}`;
    }
</script>

<style scoped>
/* ── Overlay ── */
.pfea-overlay {
  position: fixed; inset: 0; z-index: 10030;
  background: rgba(0, 0, 0, .55);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

/* ── Dialog ── */
.pfea-dialog {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; width: min(960px, 100%);
  max-height: 90dvh; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 0 24px 60px rgba(0, 0, 0, .45);
  animation: pfea-in .22s cubic-bezier(.4, 0, .2, 1);
  outline: none;
}
@keyframes pfea-in {
  from { opacity: 0; transform: scale(.96) translateY(10px); }
  to   { opacity: 1; transform: none; }
}

/* ── Header ── */
.pfea-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.pfea-head-left { display: flex; align-items: center; gap: 10px; }
.pfea-head-icon {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: color-mix(in srgb, var(--amber) 15%, transparent);
  color: var(--amber); font-size: 18px;
  display: flex; align-items: center; justify-content: center;
}
.pfea-head-title { font-size: 14px; font-weight: 800; color: var(--text); }
.pfea-head-sub   { margin-top: 2px; display: flex; gap: 6px; flex-wrap: wrap; }
.pfea-chip {
  display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 8px;
  border-radius: 20px; border: 1px solid var(--amber);
  color: var(--amber);
  background: color-mix(in srgb, var(--amber) 10%, transparent);
}
.pfea-chip-local {
  border-color: var(--orange, #f59e0b); color: var(--orange, #f59e0b);
  background: color-mix(in srgb, var(--orange, #f59e0b) 10%, transparent);
}
.pfea-chip-srv {
  border-color: var(--blue, #60a5fa); color: var(--blue, #60a5fa);
  background: color-mix(in srgb, var(--blue, #60a5fa) 10%, transparent);
}
.pfea-close {
  background: none; border: none; color: var(--text2); font-size: 20px;
  cursor: pointer; line-height: 1; padding: 4px; border-radius: 6px;
  flex-shrink: 0; transition: color .12s;
}
.pfea-close:hover { color: var(--red); }

/* ── Toolbar ── */
/* ── Barre de progression ── */
.pfea-progress-bar {
  flex-shrink: 0; padding: 6px 14px 0;
  background: color-mix(in srgb, var(--amber) 6%, var(--surface2));
  border-bottom: 1px solid color-mix(in srgb, var(--amber) 20%, var(--border));
}
.pfea-progress-inner {
  display: flex; align-items: center; gap: 8px; margin-bottom: 5px;
}
.pfea-progress-ico {
  font-size: 13px; color: var(--amber); flex-shrink: 0;
}
.pfea-progress-txt {
  font-size: 12px; font-weight: 600; color: var(--text2); flex: 1;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pfea-progress-count {
  font-size: 11px; font-weight: 800; color: var(--amber);
  background: color-mix(in srgb, var(--amber) 12%, transparent);
  padding: 1px 7px; border-radius: 10px; flex-shrink: 0;
}
.pfea-progress-track {
  height: 3px; border-radius: 2px;
  background: color-mix(in srgb, var(--amber) 15%, var(--border));
  margin-bottom: 6px; overflow: hidden;
}
.pfea-progress-fill {
  height: 100%; border-radius: 2px;
  background: var(--amber);
  transition: width .35s ease;
}
/* Transition entrée/sortie */
.pfea-progress-fade-enter-active,
.pfea-progress-fade-leave-active { transition: opacity .25s, max-height .25s; max-height: 60px; overflow: hidden; }
.pfea-progress-fade-enter-from,
.pfea-progress-fade-leave-to   { opacity: 0; max-height: 0; }

.pfea-toolbar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.pfea-search-wrap { position: relative; flex: 1; min-width: 140px; }
.pfea-search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--text3); pointer-events: none; font-size: 15px;
}
.pfea-search {
  width: 100%; padding: 9px 34px 9px 32px;
  background: var(--surface2); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text); font-size: 13px;
  outline: none; font-family: inherit; transition: border-color .15s;
}
.pfea-search:focus { border-color: var(--amber); }
.pfea-search::placeholder { color: var(--text3); }
.pfea-search-clear {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: var(--text2); cursor: pointer;
  font-size: 14px; padding: 0;
}
.pfea-filter-group { display: flex; gap: 6px; flex-shrink: 0; }
.pfea-perpage { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.pfea-perpage-lbl { font-size: 11px; font-weight: 700; color: var(--text3); white-space: nowrap; }
.pfea-perpage-sel {
  padding: 7px 10px; border-radius: 7px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text); font-size: 12px; outline: none; cursor: pointer; font-family: inherit;
}
.pfea-refresh-btn { margin-left: 4px; }

/* ── Filtre période ── */
.pfea-period {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  flex-wrap: wrap;
  padding: 8px 14px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  background: color-mix(in srgb, var(--amber) 4%, var(--surface2));
}
.pfea-period-presets { display: flex; gap: 6px; flex-wrap: wrap; }
.pfea-period-btn {
  padding: 6px 12px; border-radius: 7px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text2); font-size: 12px; font-weight: 700;
  cursor: pointer; font-family: inherit; transition: all .12s;
}
.pfea-period-btn:hover { border-color: var(--amber); color: var(--amber); }
.pfea-period-btn-active {
  background: var(--amber) !important; border-color: var(--amber) !important;
  color: #fff !important;
}
.pfea-period-range { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pfea-period-field { display: flex; align-items: center; gap: 6px; }
.pfea-period-lbl { font-size: 11px; font-weight: 700; color: var(--text3); }
.pfea-period-input {
  padding: 6px 10px; border-radius: 7px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text); font-size: 12px; outline: none; font-family: inherit;
}
.pfea-period-input:focus { border-color: var(--amber); }
.pfea-period-input:disabled { opacity: .6; cursor: not-allowed; }

/* ── Compteur ── */
.pfea-count-bar {
  padding: 7px 18px; font-size: 12px; color: var(--text2);
  background: var(--surface2); border-bottom: 1px solid var(--border); flex-shrink: 0;
  display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
}
.pfea-count-num { font-weight: 800; color: var(--text); }

/* ── Indicateur dernier fetch serveur ── */
.pfea-lastfetch {
  margin-left: auto; display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; white-space: nowrap;
}
.pfea-lastfetch i { font-size: 13px; }
.pfea-lastfetch-fresh { color: var(--green, #34d399); }
.pfea-lastfetch-stale { color: var(--orange, #f59e0b); }

/* ── Body (scrollable) ── */
.pfea-body {
  flex: 1; overflow-y: auto;
  scrollbar-width: thin; scrollbar-color: var(--border) transparent;
}
.pfea-body::-webkit-scrollbar { width: 5px; }
.pfea-body::-webkit-scrollbar-track { background: transparent; }
.pfea-body::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

/* ── States (loading/error/empty) ── */
.pfea-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 20px; text-align: center; gap: 10px;
}
.pfea-state-icon { font-size: 40px; color: var(--text3); }
.pfea-state-spin { animation: pfea-spin .9s linear infinite; }
.pfea-state-err  { color: var(--red); }
@keyframes pfea-spin { to { transform: rotate(360deg); } }
.pfea-state p    { font-size: 14px; font-weight: 600; color: var(--text2); }
.pfea-state small { font-size: 11px; color: var(--text3); }

/* ── Table ── */
.pfea-table { width: 100%; border-collapse: collapse; }
.pfea-th {
  padding: 10px 12px; text-align: left; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .5px; color: var(--text2);
  background: color-mix(in srgb, var(--amber) 10%, var(--surface2));
  border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 1;
  white-space: nowrap;
}
.pfea-th-num    { width: 40px; }
.pfea-th-right  { text-align: right; }
.pfea-th-center { text-align: center; }

.pfea-tr {
  border-bottom: 1px solid var(--border); cursor: pointer;
  transition: background .1s;
}
.pfea-tr:hover { background: color-mix(in srgb, var(--amber) 6%, var(--surface)); }
.pfea-tr-done { opacity: .55; }
.pfea-tr:last-child { border-bottom: none; }

.pfea-td {
  padding: 10px 12px; font-size: 12px; color: var(--text);
  vertical-align: middle;
}
.pfea-td-right  { text-align: right; }
.pfea-td-center { text-align: center; }

.pfea-num-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 50%; font-size: 11px; font-weight: 800;
  background: var(--surface2); color: var(--text2);
}
.pfea-tr:hover .pfea-num-badge {
  background: color-mix(in srgb, var(--amber) 20%, transparent);
  color: var(--amber);
}
.pfea-doc-badge {
  display: inline-block; padding: 3px 8px; border-radius: 6px;
  background: var(--surface2); border: 1px solid var(--border);
  font-size: 11px; font-weight: 700; font-family: ui-monospace, monospace; color: var(--text);
}
.pfea-client-row { display: flex; align-items: center; gap: 8px; }
.pfea-avatar {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  background: var(--blue); color: #fff;
  font-size: 10px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}
.pfea-client-name { font-size: 12px; font-weight: 600; color: var(--text); max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pfea-table-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 700;
  background: color-mix(in srgb, var(--purple) 12%, transparent);
  color: var(--purple);
}
.pfea-amount { font-size: 13px; font-weight: 800; color: var(--green); }
.pfea-text-muted { font-size: 11px; color: var(--text2); }
.pfea-dash { color: var(--text3); }

/* ── Status badges ── */
.pfea-status-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700;
  white-space: nowrap;
}
.pfea-status-dot {
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
}
.pfea-dot-pending { background: var(--orange, #f59e0b); }
.pfea-dot-sent    { background: var(--blue, #60a5fa); }
.pfea-dot-failed  { background: var(--red, #f87171); }
.pfea-dot-done    { background: var(--green, #34d399); }
.pfea-status-pending { color: var(--orange, #f59e0b); background: color-mix(in srgb, var(--orange, #f59e0b) 12%, transparent); }
.pfea-status-sent    { color: var(--blue, #60a5fa); background: color-mix(in srgb, var(--blue, #60a5fa) 12%, transparent); }
.pfea-status-failed  { color: var(--red, #f87171); background: color-mix(in srgb, var(--red, #f87171) 12%, transparent); }
.pfea-status-done    { color: var(--green, #34d399); background: color-mix(in srgb, var(--green, #34d399) 12%, transparent); }

/* ── Origin badges ── */
.pfea-origin-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 6px; font-size: 10px; font-weight: 700;
  white-space: nowrap;
}
.pfea-origin-local { color: var(--orange, #f59e0b); background: color-mix(in srgb, var(--orange, #f59e0b) 10%, transparent); }
.pfea-origin-server { color: var(--blue, #60a5fa); background: color-mix(in srgb, var(--blue, #60a5fa) 10%, transparent); }

/* ── Action buttons ── */
.pfea-action-group { display: flex; gap: 4px; align-items: center; justify-content: center; }

.pfea-load-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px; border-radius: 7px; border: none; cursor: pointer;
  background: color-mix(in srgb, var(--amber) 15%, transparent);
  color: var(--amber); font-size: 12px; font-weight: 700;
  font-family: inherit; transition: background .1s;
}
.pfea-load-btn:hover:not(:disabled) { background: color-mix(in srgb, var(--amber) 25%, transparent); }
.pfea-load-btn:disabled { opacity: .35; cursor: not-allowed; }

.pfea-del-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; padding: 0; border-radius: 7px; border: none; cursor: pointer;
  background: color-mix(in srgb, var(--red) 10%, transparent);
  color: var(--red); font-size: 14px;
  font-family: inherit; transition: background .1s;
}
.pfea-del-btn:hover:not(:disabled) { background: color-mix(in srgb, var(--red) 22%, transparent); }
.pfea-del-btn:disabled { opacity: .45; cursor: not-allowed; }

.pfea-spin { display: inline-block; animation: pfea-spin .9s linear infinite; }

/* Avertissement « lignes encore présentes » sous le bouton supprimer. */
.pfea-block-lignes {
  margin-top: 6px; display: flex; flex-direction: column; align-items: center; gap: 4px;
}
.pfea-block-msg {
  font-size: 10px; font-weight: 700; color: var(--orange, #f59e0b);
  white-space: nowrap;
}
.pfea-supp-lignes-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 9px; border-radius: 7px; border: none; cursor: pointer;
  background: color-mix(in srgb, var(--orange, #f59e0b) 15%, transparent);
  color: var(--orange, #f59e0b);
  font-size: 10px; font-weight: 700; font-family: inherit; white-space: nowrap;
}
.pfea-supp-lignes-btn:hover:not(:disabled) { background: color-mix(in srgb, var(--orange, #f59e0b) 26%, transparent); }
.pfea-supp-lignes-btn:disabled { opacity: .45; cursor: not-allowed; }

/* Hide cols on smaller screens */
.pfea-col-hide-sm { display: table-cell; }
.pfea-col-hide-md { display: table-cell; }

/* ── Footer / Pagination ── */
.pfea-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-top: 1px solid var(--border); flex-shrink: 0;
  background: color-mix(in srgb, var(--surface2) 50%, var(--surface));
  flex-wrap: wrap;
}
.pfea-page-info { font-size: 12px; color: var(--text2); }
.pfea-page-info strong { color: var(--text); }
.pfea-page-total { color: var(--text3); margin-left: 6px; }
.pfea-page-nav { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }

.pfea-page-btn {
  min-width: 32px; height: 32px; padding: 0 6px; border-radius: 7px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text2); font-size: 12px; font-weight: 700;
  cursor: pointer; font-family: inherit; transition: all .1s;
  display: flex; align-items: center; justify-content: center;
}
.pfea-page-btn:hover:not(:disabled) { border-color: var(--amber); color: var(--amber); }
.pfea-page-btn:disabled { opacity: .35; cursor: not-allowed; }
.pfea-page-btn-active {
  background: var(--amber) !important; border-color: var(--amber) !important;
  color: #fff !important;
}
.pfea-page-dots { font-size: 12px; color: var(--text3); padding: 0 4px; }

.pfea-btn-sec {
  padding: 7px 16px; border-radius: 8px; margin-left: 8px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text2); font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: all .12s;
}
.pfea-btn-sec:hover { border-color: var(--red); color: var(--red); }

/* ── Mobile bottom-sheet ── */
@media (max-width: 640px) {
  .pfea-overlay { align-items: flex-end; padding: 0; }
  .pfea-dialog {
    width: 100%; max-height: 94dvh;
    border-radius: 16px 16px 0 0;
    animation: pfea-slide-up .25s cubic-bezier(.4, 0, .2, 1);
  }
  .pfea-col-hide-sm { display: none; }
  .pfea-col-hide-md { display: none; }
  .pfea-page-total  { display: none; }
  .pfea-filter-group { flex: 1 1 100%; }
}
@keyframes pfea-slide-up {
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

@media (max-width: 860px) {
  .pfea-col-hide-md { display: none; }
}
</style>
